import { Search, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArticleCard } from "@/components/public/ArticleCard";
import { FactCheckCard } from "@/components/public/FactCheckCard";
import { useI18n } from "@/lib/i18n";
import { useArticles } from "@/hooks/useArticles";
import { useVerifications } from "@/hooks/useVerifications";
import { useCategories } from "@/hooks/useCategories";
import { TYPE_CONTENU_OPTIONS, VERDICT_OPTIONS, LANGUES_CONFIG } from "@/lib/enums";
import type { CategorieDto } from "@/types/categorie.types";
import { adaptArticles, adaptVerifications } from "@/lib/adapters";
import { useSeo } from "@/hooks/useSeo";

function SearchPage() {
  const { t, locale } = useI18n();
  // La query vit dans l'URL (?q=...) : c'est ce qui permet à la boîte de
  // recherche Google (WebSite/SearchAction) de pointer vers une page qui
  // affiche vraiment les résultats correspondants.
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") ?? "");

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (q) next.set("q", q); else next.delete("q");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  useSeo({
    title: q ? `${t("nav.search")}: ${q} — Ramba News` : `${t("nav.search")} — Ramba News`,
    description: t("search.placeholder"),
    path: q ? `/search?q=${encodeURIComponent(q)}` : "/search",
    noindex: true,
  });

  const [type, setType] = useState<"all" | "article" | "factcheck">("all");
  const [categorieId, setCategorieId] = useState("");
  const [contentType, setContentType] = useState("");
  const [verdict, setVerdict] = useState("");
  const [langue, setLangue] = useState("");

  const { data: categoriesData } = useCategories();
  const categories: CategorieDto[] = Array.isArray(categoriesData) ? categoriesData : [];

  const { data: articlesData, isLoading: loadingArticles } = useArticles(0, 100, "PUBLIE", {
    categorieId: categorieId ? Number(categorieId) : undefined,
    type: contentType || undefined,
    langue: langue || undefined,
  });
  const { data: verificationsData, isLoading: loadingVerifs } = useVerifications({
    categorieId: categorieId ? Number(categorieId) : undefined,
    verdict: verdict || undefined,
    estPublic: true,
  });

  const rawArticles = Array.isArray(articlesData)
    ? articlesData
    : (articlesData as any)?.content ?? [];
  const allArticles = adaptArticles(rawArticles);

  const rawVerifs = Array.isArray(verificationsData) ? verificationsData : [];
  const allVerifs = adaptVerifications(rawVerifs);

  const articles = allArticles.filter(
    (a) => !q || (a.title[locale] ?? a.title.fr ?? "").toLowerCase().includes(q.toLowerCase())
  );
  const fcs = allVerifs.filter(
    (f) => !q || (f.claim[locale] ?? f.claim.fr ?? "").toLowerCase().includes(q.toLowerCase())
  );

  const isLoading = loadingArticles || loadingVerifs;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 md:py-14">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-verified">{t("nav.search")}</div>
      <h1 className="mt-2 font-serif text-3xl md:text-5xl font-semibold tracking-tight">{t("nav.search")}</h1>

      <div className="mt-8 flex items-center gap-3 rounded-md border border-input bg-card px-4 py-3">
        <Search className="h-4 w-4 text-muted-foreground" />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("search.placeholder")}
          className="flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground"
        />
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 text-xs">
        <div>
          <label className="font-semibold uppercase tracking-wider text-muted-foreground">{t("filter.type")}</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as "all" | "article" | "factcheck")}
            className="mt-1.5 block w-full rounded-md border border-input bg-background px-2 py-2"
          >
            <option value="all">{t("filter.all")}</option>
            <option value="article">{t("search.articles")}</option>
            <option value="factcheck">{t("search.verifications")}</option>
          </select>
        </div>
        <div>
          <label className="font-semibold uppercase tracking-wider text-muted-foreground">{t("filter.category")}</label>
          <select
            value={categorieId}
            onChange={(e) => setCategorieId(e.target.value)}
            className="mt-1.5 block w-full rounded-md border border-input bg-background px-2 py-2"
          >
            <option value="">{t("filter.all")}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {locale === "fr" ? c.libelleFr : c.libelleEn}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="font-semibold uppercase tracking-wider text-muted-foreground">{t("filter.contentType")}</label>
          <select
            value={contentType}
            onChange={(e) => setContentType(e.target.value)}
            className="mt-1.5 block w-full rounded-md border border-input bg-background px-2 py-2"
          >
            <option value="">{t("filter.all")}</option>
            {TYPE_CONTENU_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="font-semibold uppercase tracking-wider text-muted-foreground">{t("filter.verdict")}</label>
          <select
            value={verdict}
            onChange={(e) => setVerdict(e.target.value)}
            className="mt-1.5 block w-full rounded-md border border-input bg-background px-2 py-2"
          >
            <option value="">{t("filter.all")}</option>
            {VERDICT_OPTIONS.map((v) => (
              <option key={v.value} value={v.value}>{v.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="font-semibold uppercase tracking-wider text-muted-foreground">{t("filter.language")}</label>
          <select
            value={langue}
            onChange={(e) => setLangue(e.target.value)}
            className="mt-1.5 block w-full rounded-md border border-input bg-background px-2 py-2"
          >
            <option value="">{t("filter.all")}</option>
            {LANGUES_CONFIG.map((l) => (
              <option key={l.code} value={l.apiCode}>{l.label}</option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="mt-16 flex items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">{t("common.loading")}</span>
        </div>
      ) : (
        <>
          {(type === "all" || type === "article") && (
            <section className="mt-12">
              <h2 className="text-lg font-semibold">{t("search.articles")} ({articles.length})</h2>
              <div className="mt-5 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {articles.map((a) => <ArticleCard key={a.id} article={a} variant="compact" />)}
              </div>
            </section>
          )}
          {(type === "all" || type === "factcheck") && (
            <section className="mt-12">
              <h2 className="text-lg font-semibold">{t("search.verifications")} ({fcs.length})</h2>
              <div className="mt-5 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {fcs.map((f) => <FactCheckCard key={f.id} fc={f} />)}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default SearchPage;
